import { useEffect, useId, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { AppError } from '@shared/api/error'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { toast } from '@shared/lib/toast/toast-store'
import { Banner, Button, Card, StatusBadge } from '@shared/design-system/ui'
import type {
  AttachBankAccountInput,
  BankAccount,
  BankAccountReadiness,
  KycOnboarding,
} from '../api/verification'
import { useAttachBankAccount, useMakeDefaultBankAccount } from '../api/verification'
import { describeBankAccountError, describeBankReadiness } from '../lib/bank-account-feedback'
import type { BankAccountErrorFeedback } from '../lib/bank-account-feedback'
import {
  account,
  accountDetails,
  accountHeader,
  accountList,
  bankContent,
  accountName,
  accountNumber,
  actionRow,
  badges,
  pendingNote,
  profileAction,
  section,
} from './bank-account-section.css'
import { BankAccountForm } from './BankAccountForm'
import {
  completedSummary,
  formCard,
  sectionCopy,
  sectionHeader,
  sectionTitle,
} from './onboarding.css'
import { onboardingStepStatus } from '../model/onboarding-status'

export const BANK_SECTION_ANCHOR = 'conta-bancaria'

type InlineBankAccountErrorFeedback = Extract<
  BankAccountErrorFeedback,
  { kind: 'message' | 'fields' | 'profile' }
>

const toastTitle = 'Não foi possível atualizar a conta bancária'

const errorStatusLabels = {
  errored: { label: 'Repasse falhou', tone: 'closed' },
  verification_failed: { label: 'Não validada', tone: 'closed' },
} as const

const roleLabels = {
  active: { label: 'Conta ativa', tone: 'success' },
  pending_replacement: { label: 'Tentativa de substituição', tone: 'awaiting' },
  previous: { label: 'Conta anterior', tone: 'neutral' },
} as const

export function BankAccountSection({
  bank,
  identityPending = false,
  onboarding,
}: {
  bank: { accounts: BankAccount[]; readiness: BankAccountReadiness } | null
  identityPending?: boolean
  onboarding?: KycOnboarding
}) {
  const registrationFeedbackEnabled = useFeatureFlag('connectRegistrationFeedback')
  const [feedback, setFeedback] = useState<InlineBankAccountErrorFeedback | null>(null)
  const [formExpanded, setFormExpanded] = useState(false)
  const [listExpanded, setListExpanded] = useState(false)
  const bankContentId = useId()
  const sectionRef = useRef<HTMLDivElement>(null)
  const bankLoaded = bank !== null
  // "Gerenciar conta" in the settings links here; the section appears only after the view loads.
  useEffect(() => {
    if (window.location.hash === `#${BANK_SECTION_ANCHOR}`) {
      sectionRef.current?.scrollIntoView?.({ block: 'start' })
    }
  }, [bankLoaded])
  const attachBankAccount = useAttachBankAccount()
  const makeDefaultBankAccount = useMakeDefaultBankAccount()
  const activeAccount = bank?.accounts.find((bankAccount) => bankAccount.role === 'active')
  const mutationPending = attachBankAccount.isPending || makeDefaultBankAccount.isPending
  const showForm = activeAccount === undefined || formExpanded
  const status = onboardingStepStatus(
    onboarding?.steps.bank ?? 'unavailable',
    onboarding?.badge === 'approved',
  )
  const hasPendingReplacement =
    bank?.accounts.some((bankAccount) => bankAccount.role === 'pending_replacement') ?? false
  const bankDone =
    onboarding?.steps.bank === 'done' && activeAccount !== undefined && !hasPendingReplacement
  const bankCollapsed = bankDone && !formExpanded && !listExpanded

  if (!bank || onboarding?.steps.bank === 'unavailable') {
    return (
      <div id={BANK_SECTION_ANCHOR} ref={sectionRef} data-testid="onboarding-bank-section">
        <Card className={formCard}>
          <div className={sectionHeader}>
            <h2 className={sectionTitle} tabIndex={-1}>
              3. Conta bancária
            </h2>
            <StatusBadge size="sm" tone={status.tone} label={status.label} />
          </div>
          <p className={sectionCopy}>
            {bank && onboarding?.steps.profile !== 'done'
              ? 'Disponível depois que os dados cadastrais estiverem salvos e corretos.'
              : 'Disponível após salvar os dados cadastrais.'}
          </p>
        </Card>
      </div>
    )
  }

  function handleError(error: unknown) {
    if (error instanceof AppError) {
      const mapped = describeBankAccountError(
        error.status,
        error.code,
        activeAccount !== undefined,
        error.param,
        registrationFeedbackEnabled,
      )
      if (mapped.kind === 'refetch') {
        setFeedback(null)
      } else if (mapped.kind === 'retry' || mapped.kind === 'switch_failed') {
        setFeedback(null)
        toast.error({ title: toastTitle, description: mapped.message })
        // The pending replacement row in the list is where the switch is retried.
        if (mapped.kind === 'switch_failed') setFormExpanded(false)
      } else {
        setFeedback(mapped)
      }
      return
    }

    setFeedback(null)
    toast.error(
      getUserFacingError(error, {
        title: toastTitle,
        fallback: 'Não foi possível concluir a ação. Tente novamente.',
      }),
    )
  }

  // Inline errors sit next to the action that raised them: the form's submit button when it is open.
  const errorBanner =
    feedback && feedback.kind !== 'fields' ? <BankAccountErrorBanner feedback={feedback} /> : null

  async function attach(input: AttachBankAccountInput): Promise<boolean> {
    setFeedback(null)
    try {
      await attachBankAccount.mutateAsync(input)
      setFormExpanded(false)
      setListExpanded(false)
      return true
    } catch (error) {
      handleError(error)
      return false
    }
  }

  async function selectDefaultAccount(bankAccountId: string) {
    setFeedback(null)
    try {
      await makeDefaultBankAccount.mutateAsync(bankAccountId)
      setFormExpanded(false)
      setListExpanded(false)
    } catch (error) {
      handleError(error)
    }
  }

  return (
    <div id={BANK_SECTION_ANCHOR} ref={sectionRef} data-testid="onboarding-bank-section">
      <Card className={formCard}>
        <section className={section}>
          <div className={sectionHeader}>
            <h2 className={sectionTitle} tabIndex={-1}>
              3. Conta bancária
            </h2>
            <StatusBadge size="sm" tone={status.tone} label={status.label} />
          </div>
          <p className={sectionCopy} role="status">
            {identityPending
              ? 'A conta bancária é a próxima etapa. Você pode cadastrá-la enquanto a verificação de identidade está em análise.'
              : describeBankReadiness(bank.readiness)}
          </p>

          {showForm ? null : errorBanner}

          {bankCollapsed ? (
            <div className={completedSummary}>
              <span>
                {activeAccount.bankName ?? 'Banco'} · •••• {activeAccount.last4 ?? '----'} · Padrão
              </span>
              <Button
                variant="secondary"
                disabled={mutationPending}
                aria-expanded={false}
                aria-controls={bankContentId}
                onClick={() => setListExpanded(true)}
              >
                Ver contas
              </Button>
            </div>
          ) : null}

          <div id={bankContentId} className={bankContent} hidden={bankCollapsed}>
            {bank.accounts.length ? (
              <ul className={accountList} aria-label="Contas bancárias cadastradas">
                {bank.accounts.map((bankAccount) => (
                  <BankAccountRow
                    key={bankAccount.id}
                    bankAccount={bankAccount}
                    disabled={mutationPending}
                    registrationFeedbackEnabled={registrationFeedbackEnabled}
                    onMakeDefault={() => void selectDefaultAccount(bankAccount.id)}
                  />
                ))}
              </ul>
            ) : null}

            {activeAccount ? (
              <div className={actionRow}>
                <Button
                  variant="secondary"
                  disabled={mutationPending}
                  aria-expanded={formExpanded}
                  onClick={() => setFormExpanded((expanded) => !expanded)}
                >
                  {bankDone
                    ? formExpanded
                      ? 'Cancelar'
                      : 'Adicionar outra conta'
                    : showForm
                      ? 'Cancelar troca'
                      : 'Trocar conta'}
                </Button>
                {bankDone ? (
                  <Button
                    variant="secondary"
                    disabled={mutationPending}
                    aria-expanded
                    aria-controls={bankContentId}
                    onClick={() => {
                      setFormExpanded(false)
                      setListExpanded(false)
                    }}
                  >
                    Ocultar
                  </Button>
                ) : null}
              </div>
            ) : null}

            {showForm ? (
              <BankAccountForm
                replacing={activeAccount !== undefined}
                registrationFeedbackEnabled={registrationFeedbackEnabled}
                disabled={mutationPending}
                serverErrors={feedback?.kind === 'fields' ? feedback.fields : undefined}
                error={errorBanner}
                onChange={() => setFeedback(null)}
                onSubmit={attach}
              />
            ) : null}
          </div>
        </section>
      </Card>
    </div>
  )
}

function BankAccountErrorBanner({
  feedback,
}: {
  feedback: Exclude<InlineBankAccountErrorFeedback, { kind: 'fields' }>
}) {
  return (
    <Banner tone="danger" title="Não foi possível concluir a ação">
      {feedback.message}
      {feedback.kind === 'profile' ? (
        <>
          {' '}
          <Link className={profileAction} to={feedback.actionTo}>
            {feedback.actionLabel}
          </Link>
        </>
      ) : null}
    </Banner>
  )
}

function BankAccountRow({
  bankAccount,
  disabled,
  registrationFeedbackEnabled,
  onMakeDefault,
}: {
  bankAccount: BankAccount
  disabled: boolean
  registrationFeedbackEnabled: boolean
  onMakeDefault: () => void
}) {
  const role = roleLabels[bankAccount.role]
  const status =
    bankAccount.status in errorStatusLabels
      ? errorStatusLabels[bankAccount.status as keyof typeof errorStatusLabels]
      : null

  return (
    <li className={account} data-testid={`onboarding-bank-card-${bankAccount.id}`}>
      <div className={accountHeader}>
        <div className={badges}>
          <StatusBadge tone={role.tone} label={role.label} />
          {status ? <StatusBadge tone={status.tone} label={status.label} /> : null}
        </div>
      </div>
      <div className={accountDetails}>
        <p className={accountName}>{bankAccount.bankName ?? 'Banco'}</p>
        <p className={accountNumber}>••••{bankAccount.last4 ? ` ${bankAccount.last4}` : ''}</p>
      </div>
      {bankAccount.role === 'pending_replacement' ? (
        <>
          <p className={pendingNote}>
            {registrationFeedbackEnabled
              ? 'A troca não foi concluída. A conta atual permanece cadastrada.'
              : 'A troca não foi concluída. A conta ativa continua recebendo repasses.'}
          </p>
          <Button variant="secondary" size="sm" disabled={disabled} onClick={onMakeDefault}>
            Tentar novamente
          </Button>
        </>
      ) : null}
      {bankAccount.role === 'previous' ? (
        <div className={actionRow}>
          <Button variant="secondary" disabled={disabled} onClick={onMakeDefault}>
            Usar esta conta
          </Button>
        </div>
      ) : null}
    </li>
  )
}
